import type {
  CatalogIndex,
  CommercialAddModulesRequest,
  CommercialAddModulesResult,
  CommercialCheckoutCompleteResult,
  CommercialCheckoutResult,
  CommercialPaymentProvidersStatus,
  CommercialSignupRequest,
  CommercialTrialResult,
  ConfigureSubmissionRequest,
  ConfigureSubmissionResponse,
  DocArticle,
  LeadRequest,
  MarketplaceItem,
  PartnersIndex,
  PricingGridsResponse,
  PricingQuoteResponse,
  SearchHit,
  TenantSlugCheckResponse,
  TenantSlugSuggestResponse,
} from "@itaas/pep-contracts";
import { PEP_API_BASE } from "../config";

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${PEP_API_BASE}${path}`, init);
  if (!res.ok) {
    const text = await res.text();
    let message = `API ${path}: ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] };
      if (typeof parsed.message === "string") message = parsed.message;
      else if (Array.isArray(parsed.message)) message = parsed.message.join(" ");
    } catch {
      if (text.trim()) message = text.slice(0, 240);
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

/** POST Configure — returns success:false without throwing when notification failed but lead persisted. */
async function fetchConfigureSubmission(
  path: string,
  init?: RequestInit,
): Promise<ConfigureSubmissionResponse> {
  const res = await fetch(`${PEP_API_BASE}${path}`, init);
  const text = await res.text();
  let parsed: ConfigureSubmissionResponse | { message?: string | string[]; statusCode?: number } | null =
    null;
  try {
    parsed = text ? (JSON.parse(text) as ConfigureSubmissionResponse) : null;
  } catch {
    parsed = null;
  }

  if (
    parsed &&
    typeof parsed === "object" &&
    "success" in parsed &&
    "reference" in parsed &&
    "notificationStatus" in parsed
  ) {
    return parsed as ConfigureSubmissionResponse;
  }

  if (!res.ok) {
    let message = `API ${path}: ${res.status}`;
    if (parsed && typeof parsed === "object" && "message" in parsed) {
      const m = parsed.message;
      if (typeof m === "string") message = m;
      else if (Array.isArray(m)) message = m.join(" ");
    } else if (text.trim()) {
      message = text.slice(0, 240);
    }
    throw new Error(message);
  }

  throw new Error(`API ${path}: unexpected response`);
}
export function getProducts() {
  return fetchJson<CatalogIndex["products"]>("/catalog/products");
}

export function getProduct(slug: string) {
  return fetchJson<CatalogIndex["products"][0] & { complementary: CatalogIndex["products"] }>(
    `/catalog/products/${slug}`,
  );
}

export function getProductContent(slug: string) {
  return fetchJson<import("@itaas/pep-contracts").ProductContent>(
    `/product-content/${encodeURIComponent(slug)}`,
  );
}

export function getSolutions() {
  return fetchJson<CatalogIndex["solutions"]>("/catalog/solutions");
}

export function getIndustries() {
  return fetchJson<CatalogIndex["industries"]>("/catalog/industries");
}

export function getPackageConfigs() {
  return fetchJson<import("@itaas/pep-contracts").PackageConfigsIndex>("/catalog/package-configs");
}

export function getPricingQuote(
  product: string,
  edition = "professional",
  currency: "USD" | "CDF" | "EUR" = "USD",
) {
  const params = new URLSearchParams({ product, edition, currency });
  return fetchJson<PricingQuoteResponse>(`/pricing/quote?${params}`);
}

export function getPricingGrids() {
  return fetchJson<PricingGridsResponse>("/pricing/grids");
}

export function submitLead(payload: LeadRequest) {
  return fetchJson<{ id: string; createdAt: string; status: string }>("/leads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function submitContactRequest(
  payload: import("@itaas/pep-contracts").ContactRequestInput,
) {
  return fetchJson<import("@itaas/pep-contracts").ContactSubmissionResponse>(
    "/contact-requests",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function submitConfigureSubmission(payload: ConfigureSubmissionRequest) {
  return fetchConfigureSubmission("/configure/submissions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function addCommercialModules(payload: CommercialAddModulesRequest) {
  return fetchJson<CommercialAddModulesResult>("/commercial/add-modules", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function startCommercialTrial(payload: CommercialSignupRequest) {
  return fetchJson<CommercialTrialResult>("/commercial/start-trial", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, mode: "trial" }),
  });
}

export function createCommercialCheckout(payload: CommercialSignupRequest) {
  return fetchJson<CommercialCheckoutResult>("/commercial/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, mode: "pay" }),
  });
}

export function getCheckoutResult(sessionId: string) {
  return fetchJson<CommercialCheckoutCompleteResult>(
    `/commercial/checkout/result?session_id=${encodeURIComponent(sessionId)}`,
  );
}

export function getCommercialPaymentProviders() {
  return fetchJson<CommercialPaymentProvidersStatus>("/commercial/payment-providers");
}

export function getProvisionStatus() {
  return fetchJson<{
    iamConfigured: boolean;
    iamApiUrl: string;
    iamReachable: boolean;
    iamHealthStatus?: number;
    smtpConfigured: boolean;
    onlinePaymentsEnabled: boolean;
    tenantAdminEmailDomain: string;
  }>("/commercial/provision-status");
}

export function suggestCommercialTenantSlugs(company: string, city?: string) {
  const params = new URLSearchParams({ company });
  if (city?.trim()) params.set("city", city.trim());
  return fetchJson<TenantSlugSuggestResponse>(`/commercial/suggest-slugs?${params}`);
}

export function checkCommercialTenantSlug(slug: string) {
  return fetchJson<TenantSlugCheckResponse>(
    `/commercial/check-slug?slug=${encodeURIComponent(slug.trim())}`,
  );
}

export function getDocs(product?: string, category?: string) {
  const params = new URLSearchParams();
  if (product) params.set("product", product);
  if (category) params.set("category", category);
  const q = params.toString();
  return fetchJson<Omit<DocArticle, "content">[]>(`/docs${q ? `?${q}` : ""}`);
}

export function getDoc(id: string) {
  return fetchJson<DocArticle>(`/docs/${id}`);
}

export function searchAll(q: string) {
  return fetchJson<SearchHit[]>(`/search?q=${encodeURIComponent(q)}`);
}

export function getMarketplaceItems(type?: string, product?: string) {
  const params = new URLSearchParams();
  if (type) params.set("type", type);
  if (product) params.set("product", product);
  const q = params.toString();
  return fetchJson<MarketplaceItem[]>(`/marketplace/items${q ? `?${q}` : ""}`);
}

export function getMarketplaceItem(slug: string) {
  return fetchJson<MarketplaceItem>(`/marketplace/items/${slug}`);
}

export function getPartners() {
  return fetchJson<Pick<PartnersIndex, "programTiers" | "partners">>(`/partners`);
}

export function getPartner(slug: string) {
  return fetchJson<PartnersIndex["partners"][0] & { marketplaceItems: MarketplaceItem[] }>(
    `/partners/${slug}`,
  );
}
